import React, { useState } from 'react';
import { Box, Button, Card, CardContent, Chip, Container, Grid, Paper, Stack, TextField, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { useTranslation } from 'react-i18next';
import { useCategories } from '../../common/hooks/useReferenceData';
import useAuth from '../../common/hooks/useAuth';
import { REMOTE_NATION } from '../../common/hooks/useReferenceData';
import { brand } from '../../theme';
import { SITE_URL } from '../../common/site';

export default function HomeLandingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuth } = useAuth();
  const categories = useCategories();
  const [query, setQuery] = useState('');

  const search = (e) => {
    e.preventDefault();
    navigate('/posts', { state: { textSearch: query.trim() } });
  };

  const popular = categories.length
    ? categories.slice(0, 8).map((c) => c.categoryName)
    : ['Developer', 'Marketing', 'Sales', 'Customer Care', 'Finance', 'Engineering'];

  const features = [
    { icon: <TravelExploreRoundedIcon />, color: brand.primary, title: t('home.feature1Title'), desc: t('home.feature1Text') },
    { icon: <HomeWorkRoundedIcon />, color: '#059669', title: t('home.feature2Title'), desc: t('home.feature2Text') },
    { icon: <BoltRoundedIcon />, color: brand.pink, title: t('home.feature3Title'), desc: t('home.feature3Text') },
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <Helmet>
        <title>PlaceYourJob – Find and post jobs worldwide</title>
        <link rel="canonical" href={`${SITE_URL}/`} />
      </Helmet>

      {/* Hero */}
      <Box sx={{ position: 'relative', background: brand.softGradient, pt: { xs: 6, md: 12 }, pb: { xs: 8, md: 12 } }}>
        <Box aria-hidden sx={{ position: 'absolute', top: -120, left: -120, width: { xs: 320, md: 520 }, height: { xs: 320, md: 520 }, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(brand.primary, 0.22)} 0%, transparent 70%)`, animation: 'blob 18s ease-in-out infinite' }} />
        <Box aria-hidden sx={{ position: 'absolute', bottom: -160, right: -120, width: { xs: 300, md: 480 }, height: { xs: 300, md: 480 }, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(brand.pink, 0.18)} 0%, transparent 70%)`, animation: 'blob 22s ease-in-out infinite reverse' }} />

        <Container maxWidth="md" sx={{ position: 'relative', textAlign: 'center' }}>
          <Chip
            icon={<BoltRoundedIcon />}
            label={t('home.badge')}
            sx={{ mb: 3, backgroundColor: '#FFF', border: `1px solid ${brand.border}`, fontWeight: 700, '& .MuiChip-icon': { color: brand.pink } }}
          />
          <Typography
            component="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '2.35rem', sm: '3.4rem', md: '4.4rem' },
              lineHeight: 1.05,
              letterSpacing: '-0.04em',
              mb: 2.5,
              animation: 'slideUpFade .7s ease-out both',
            }}
          >
            {t('home.heroTitle')}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: { xs: '1.05rem', md: '1.25rem' }, maxWidth: 640, mx: 'auto', mb: { xs: 4, md: 5 }, animation: 'slideUpFade .7s .1s ease-out both' }}>
            {t('home.heroSubtitle')}
          </Typography>

          <Paper
            component="form"
            onSubmit={search}
            role="search"
            elevation={0}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              p: 1,
              borderRadius: 4,
              border: `1px solid ${brand.border}`,
              boxShadow: '0 24px 60px -30px rgba(79,70,229,0.55)',
              maxWidth: 680,
              mx: 'auto',
              animation: 'slideUpFade .7s .2s ease-out both',
            }}
          >
            <TextField
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('home.searchPlaceholder')}
              variant="standard"
              fullWidth
              InputProps={{ disableUnderline: true, startAdornment: <SearchRoundedIcon sx={{ color: 'text.secondary', mr: 1 }} /> }}
              inputProps={{ 'aria-label': t('home.searchPlaceholder'), enterKeyHint: 'search' }}
              sx={{ px: 1.5, justifyContent: 'center', '& input': { py: 1.5, fontSize: '1.05rem' } }}
            />
            <Button type="submit" variant="contained" size="large" sx={{ px: 4, flexShrink: 0 }}>
              {t('home.searchButton')}
            </Button>
          </Paper>

          <Stack direction="row" justifyContent="center" sx={{ mt: 3, gap: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={<HomeWorkRoundedIcon />}
              label={t('jobs.remoteJobs')}
              onClick={() => navigate('/posts', { state: { nation: REMOTE_NATION, isRemote: true } })}
              sx={{ backgroundColor: '#ECFDF5', color: '#047857', fontWeight: 700, '& .MuiChip-icon': { color: 'inherit' } }}
            />
            {popular.map((name) => (
              <Chip
                key={name}
                label={name}
                onClick={() => navigate('/posts', { state: { textSearch: name } })}
                sx={{ backgroundColor: '#FFF', border: `1px solid ${brand.border}`, fontWeight: 600 }}
              />
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Features */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={3}>
          {features.map((feature, idx) => (
            <Grid item xs={12} md={4} key={feature.title} sx={{ animation: `slideUpFade .6s ${0.1 * idx}s ease-out both` }}>
              <Card sx={{ height: '100%', transition: 'transform .25s ease', '@media (hover: hover)': { '&:hover': { transform: 'translateY(-6px)' } } }}>
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Box sx={{ width: 52, height: 52, borderRadius: '16px', display: 'grid', placeItems: 'center', color: feature.color, backgroundColor: alpha(feature.color, 0.1), mb: 2.5, '& svg': { fontSize: 28 } }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 800, mb: 1 }}>{feature.title}</Typography>
                  <Typography color="text.secondary">{feature.desc}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Call to action */}
        <Paper
          elevation={0}
          sx={{
            mt: { xs: 6, md: 10 },
            p: { xs: 4, md: 7 },
            borderRadius: 6,
            color: '#FFF',
            background: brand.gradient,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box aria-hidden sx={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', top: -100, right: -60 }} />
          <Grid container spacing={3} alignItems="center" sx={{ position: 'relative' }}>
            <Grid item xs={12} md={8}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                <CampaignRoundedIcon />
                <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: '0.1em' }}>{t('home.ctaEyebrow')}</Typography>
              </Stack>
              <Typography variant="h4" component="h2" sx={{ fontWeight: 800, mb: 1 }}>{t('home.ctaTitle')}</Typography>
              <Typography sx={{ opacity: 0.9 }}>{t('home.ctaText')}</Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { md: 'right' } }}>
              <Button
                component={Link}
                to={isAuth ? '/add-post' : '/register'}
                size="large"
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{ backgroundColor: '#FFF', color: brand.primary, px: 4, width: { xs: '100%', md: 'auto' }, '&:hover': { backgroundColor: '#F1F5F9' } }}
              >
                {isAuth ? t('jobs.createJob') : t('chat.registerCta')}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
}
